import { forwardRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import { Message } from '../Message';
import type { RiskCardProps } from './RiskCard.types';
import type { Theme } from '../../theme';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const RiskCardContainer = styled.div<{ $interactive: boolean }>`
  padding: ${({ theme }) => (theme as Theme).spacing[4]};
  background: ${({ theme }) => (theme as Theme).colors.background.default};
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.sm};
  border: ${({ theme }) => (theme as Theme).borders.width.thin} solid ${({ theme }) => (theme as Theme).colors.border.light};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  animation: ${slideIn} 0.3s ease-out;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

const RiskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => (theme as Theme).spacing[3]};
  margin-bottom: ${({ theme }) => (theme as Theme).spacing[3]};
`;

const RiskTitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const RiskTitle = styled.h4`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.semibold};
  color: ${({ theme }) => (theme as Theme).colors.text.primary};
  margin: 0;
  line-height: 1.4;
`;

const RiskIndex = styled.span`
  font-size: 10px;
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.medium};
  color: ${({ theme }) => (theme as Theme).colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RiskDescription = styled.p`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  margin: 0 0 ${({ theme }) => (theme as Theme).spacing[3]};
  line-height: 1.6;
`;

const QuoteText = styled.p`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  margin: 0 0 ${({ theme }) => (theme as Theme).spacing[3]};
  line-height: 1.5;
  font-style: italic;
`;

const ExpandableSection = styled.div<{ $isExpanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '500px' : '0')};
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  transition: max-height 0.3s ease, opacity 0.2s ease, margin 0.2s ease;
  margin-top: ${({ $isExpanded, theme }) => ($isExpanded ? (theme as Theme).spacing[3] : '0')};
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 0;
  background: none;
  border: none;
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.medium};
  color: ${({ theme }) => (theme as Theme).colors.highlight};
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus-visible {
    outline: 2px solid ${({ theme }) => (theme as Theme).colors.highlight};
    outline-offset: 2px;
  }
`;

const FooterContainer = styled.div`
  margin-top: ${({ theme }) => (theme as Theme).spacing[3]};
`;

/**
 * RiskCard component - Displays an individual compliance risk
 *
 * Supports two usage patterns:
 * 1. Direct API data injection: Pass the `risk` prop with API response data
 * 2. Individual props: Pass title, description, etc. separately for custom mapping
 *
 * @example Direct API data injection
 * ```tsx
 * const apiResponse = { title: 'Rule Name', description: 'Issue found', recommendation: 'Fix it' };
 * <RiskCard risk={apiResponse} index={0} />
 * ```
 *
 * @example Individual props
 * ```tsx
 * <RiskCard
 *   title="Custom Title"
 *   description="Custom description"
 *   quote="The problematic sentence"
 *   recommendation="How to fix"
 *   index={0}
 * />
 * ```
 *
 * @example With custom labels
 * ```tsx
 * <RiskCard
 *   risk={data}
 *   indexLabelPrefix="Issue"
 *   showRecommendationText="View suggestion"
 *   hideRecommendationText="Hide suggestion"
 * />
 * ```
 */
export const RiskCard = forwardRef<HTMLDivElement, RiskCardProps>(
  (
    {
      // API data injection
      risk,
      // Individual props (override risk fields)
      title,
      description,
      recommendation,
      quote,
      severity,
      // Display configuration
      index,
      indexLabelPrefix = 'Detected Risk',
      expandable = true,
      defaultExpanded = false,
      showRecommendationText = 'Show recommendation',
      hideRecommendationText = 'Hide recommendation',
      // Event handlers
      onExpandChange,
      onClick,
      // Customization
      className,
      style,
      headerContent,
      footerContent,
      // Testing
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    // Resolve props (individual props override risk fields)
    const resolvedTitle = title ?? risk?.title ?? '';
    const resolvedDescription = description ?? risk?.description ?? '';
    const resolvedRecommendation = recommendation ?? risk?.recommendation;
    const resolvedQuote = quote ?? risk?.sentence;
    const resolvedSeverity = severity ?? risk?.severity;

    const hasExpandableContent = expandable && Boolean(resolvedRecommendation);
    const isInteractive = Boolean(onClick);

    const handleExpandToggle = () => {
      const newExpanded = !isExpanded;
      setIsExpanded(newExpanded);
      onExpandChange?.(newExpanded);
    };

    const handleClick = () => {
      onClick?.();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick?.();
      }
    };

    return (
      <RiskCardContainer
        ref={ref}
        className={className}
        style={style}
        $interactive={isInteractive}
        onClick={isInteractive ? handleClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        data-testid={dataTestId || testId}
        data-id={dataId}
        data-severity={resolvedSeverity}
      >
        {headerContent ? (
          headerContent
        ) : (
          <RiskHeader>
            <RiskTitleArea>
              {index !== undefined && (
                <RiskIndex>{indexLabelPrefix} #{index + 1}</RiskIndex>
              )}
              <RiskTitle>{resolvedTitle}</RiskTitle>
            </RiskTitleArea>
          </RiskHeader>
        )}

        {resolvedDescription && (
          <RiskDescription>
            {resolvedDescription}
          </RiskDescription>
        )}

        {resolvedQuote && (
          <QuoteText>
            &ldquo;{resolvedQuote}&rdquo;
          </QuoteText>
        )}

        {hasExpandableContent && (
          <>
            <ExpandButton
              onClick={(e) => {
                e.stopPropagation();
                handleExpandToggle();
              }}
              aria-expanded={isExpanded}
              data-expanded={isExpanded}
            >
              {isExpanded ? hideRecommendationText : showRecommendationText}
            </ExpandButton>

            <ExpandableSection $isExpanded={isExpanded}>
              <Message variant="info" mode="banner" size="xs">
                {resolvedRecommendation}
              </Message>
            </ExpandableSection>
          </>
        )}

        {footerContent && (
          <FooterContainer>
            {footerContent}
          </FooterContainer>
        )}
      </RiskCardContainer>
    );
  }
);

RiskCard.displayName = 'RiskCard';

export default RiskCard;
